import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import * as z from 'zod';
import { SchemaSection } from '@rossum/api-client/schemas';
import { getRossumAgentUrl } from '../../../../chat/api';
import { getAgentHeaders } from '../../../../chat/hooks/useChatApi';
import type { LookupResult } from './lookupResult';
import { lookupResultSchema } from './lookupResult';
import type { SuggestLookupVariables } from './useSuggestLookup';
import { findSectionIdForField } from './utils';

const MALFORMED_SUGGESTION =
  'The lookup suggester returned a response we cannot apply.';

/**
 * Either a configuration or the agent's own account of why there isn't one. Answering in words is a
 * normal outcome — a hint naming columns the dataset does not have is the common case — so it comes
 * back as a success the caller can show, not an error it has to invent copy for.
 */
const suggestResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('configured'),
    configuration: lookupResultSchema,
  }),
  z.object({ status: z.literal('needs_input'), message: z.string().min(1) }),
]);

type SuggestOutcome = z.infer<typeof suggestResponseSchema>;

/** What a prompt surface needs from a suggester, with nothing about which backend answered. */
export type LookupSuggester = {
  mutate: (variables: SuggestLookupVariables) => void;
  isLoading: boolean;
  isError: boolean;
  /**
   * What the agent said when it configured nothing — a hint naming columns the dataset does not
   * have, usually. Shown in place of the generic copy, because it is the only thing that tells the
   * user what to type instead.
   */
  message?: string;
  reset: () => void;
};

/**
 * The agentic lookup suggester: an agent writes the configuration itself, rather than a single pass
 * over a hint. It reads the dataset's real column names first, and checks a query returns the right
 * record before configuring it — which is what the one-shot endpoint cannot do.
 *
 * It answers in one request. The agent is reached through the backend's own `computed-fields/suggest`
 * route rather than Mastra's agent API, so the wire contract is ours and a framework upgrade cannot
 * reshape the response. Turns are written to a memory thread keyed on `session_id`, so a follow-up
 * refines this draft instead of starting over, and the conversational surface can continue it later.
 */
export const useAgentSuggestLookup = ({
  fieldSchemaId,
  label,
  schemaContent,
  onResult,
}: {
  fieldSchemaId: string;
  label: string;
  schemaContent: SchemaSection[];
  onResult: (result: LookupResult) => void;
}): LookupSuggester => {
  const sessionIdRef = useRef(uuidv4());
  const [message, setMessage] = useState<string | undefined>(undefined);

  const {
    mutate,
    isLoading,
    isError,
    reset: resetMutation,
  } = useMutation<SuggestOutcome, unknown, SuggestLookupVariables>(
    async ({ hint, queryIndex, dataset }) => {
      const response = await fetch(
        `${getRossumAgentUrl()}/api/v1/computed-fields/suggest`,
        {
          method: 'POST',
          headers: getAgentHeaders(),
          body: JSON.stringify({
            hint,
            session_id: sessionIdRef.current,
            query_index: queryIndex,
            draft: {
              kind: 'lookup',
              field_schema_id: fieldSchemaId,
              label,
              section_id: findSectionIdForField(schemaContent, fieldSchemaId),
              schema_content: schemaContent,
              dataset,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Rossum Agent returned ${response.status} ${response.statusText}`
        );
      }

      const parsed = suggestResponseSchema.safeParse(await response.json());
      if (!parsed.success) throw new Error(MALFORMED_SUGGESTION);

      return parsed.data;
    }
  );

  return {
    mutate: useCallback(
      (variables: SuggestLookupVariables) => {
        // Both error sources have to clear together: `mutate` leaves a previous hard failure's
        // `isError` set, so without this a new prompt shows the last one's message while it runs.
        setMessage(undefined);
        resetMutation();
        mutate(variables, {
          onSuccess: outcome =>
            outcome.status === 'configured'
              ? onResult(outcome.configuration)
              : setMessage(outcome.message),
        });
      },
      [mutate, onResult, resetMutation]
    ),
    isLoading,
    // The field is unconfigured either way, and the composer's error slot is where its explanation
    // goes, so a message counts as a failed suggestion — with better wording than ours.
    isError: isError || message !== undefined,
    message,
    reset: useCallback(() => {
      setMessage(undefined);
      resetMutation();
    }, [resetMutation]),
  };
};
